import { useMemo, useState } from 'react'

import type { ReportRangeQuery } from '@/types/reports'


const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const daysAgo = (n: number) => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return ymd(d)
}

export type RangePreset = '7' | '30' | '90' | 'month' | 'last-month' | 'year' | 'custom'

/** The range a preset stands for, in the browser's calendar (the server reads the days in the store's time zone). */
export const presetRange = (preset: RangePreset, custom?: ReportRangeQuery): ReportRangeQuery => {
  const now = new Date()
  switch (preset) {
    case '7':
    case '30':
    case '90':
      return { from_date: daysAgo(Number(preset) - 1), to_date: ymd(now) }
    case 'month':
      return { from_date: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to_date: ymd(now) }
    case 'last-month':
      return { from_date: ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to_date: ymd(new Date(now.getFullYear(), now.getMonth(), 0)) }
    case 'year':
      return { from_date: ymd(new Date(now.getFullYear(), 0, 1)), to_date: ymd(now) }
    default:
      return custom ?? { from_date: daysAgo(29), to_date: ymd(now) }
  }
}

/** The page's date range: a preset (last 30 days by default) or two chosen dates. */
export const useReportRange = (initial: RangePreset = '30') => {
  const [preset, setPreset] = useState<RangePreset>(initial)
  const [custom, setCustom] = useState<ReportRangeQuery>(() => presetRange(initial))
  const range = useMemo(() => (preset === 'custom' ? custom : presetRange(preset)), [preset, custom])
  return {
    preset,
    range,
    onPresetChange: (next: RangePreset) => {
      if (next === 'custom') setCustom(range)
      setPreset(next)
    },
    onRangeChange: setCustom,
  }
}

/** A % change as StatCard expects it. */
export const trendOf = (change: number | null | undefined): { change: number | string; trend: 'up' | 'down' | 'neutral' } => {
  if (change === null) return { change: 'New', trend: 'up' }
  if (!change) return { change: 0, trend: 'neutral' }
  return { change: Math.abs(change), trend: change > 0 ? 'up' : 'down' }
}
